import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { ArrowLeft, Building2, Eye, EyeOff, KeyRound, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function makeSlugPreview(name) {
  return (name || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 50);
}

function generateStrongPassword() {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower = 'abcdefghjkmnpqrstuvwxyz';
  const nums = '23456789';
  const symbols = '@#$&!';
  const all = upper + lower + nums + symbols;
  const arr = [
    upper[Math.floor(Math.random() * upper.length)],
    lower[Math.floor(Math.random() * lower.length)],
    nums[Math.floor(Math.random() * nums.length)],
    symbols[Math.floor(Math.random() * symbols.length)],
  ];
  for (let i = 0; i < 8; i++) {
    arr.push(all[Math.floor(Math.random() * all.length)]);
  }
  return arr.sort(() => Math.random() - 0.5).join('');
}

export const CreateTenantPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    admin_email: '',
    admin_name: '',
    admin_password: '',
    plan: 'standard',
    primary_color: '#1e3a8a',
    contact_email: '',
    observacoes: '',
  });

  const slugPreview = formData.slug || makeSlugPreview(formData.name);

  const handleChange = (k, v) => setFormData((p) => ({ ...p, [k]: v }));

  const handleGeneratePassword = () => {
    const pwd = generateStrongPassword();
    setFormData((p) => ({ ...p, admin_password: pwd }));
    setShowPassword(true);
    toast.success('Senha gerada. Anote-a antes de criar o cliente!');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.admin_email || !formData.admin_name || !formData.admin_password) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    if (formData.admin_password.length < 8) {
      toast.error('A senha deve ter no mínimo 8 caracteres');
      return;
    }
    setLoading(true);
    try {
      const payload = { ...formData };
      if (!payload.slug) delete payload.slug;
      if (!payload.contact_email) delete payload.contact_email;
      if (!payload.observacoes) delete payload.observacoes;
      const { data } = await axios.post(`${BACKEND_URL}/api/platform/tenants`, payload, {
        withCredentials: true,
      });
      toast.success(`Cliente "${data.name}" criado! Admin já pode entrar.`);
      navigate('/platform');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao criar cliente');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/platform')}
            data-testid="back-to-platform-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" /> Voltar
          </Button>
          <div className="h-8 w-px bg-slate-200" />
          <div>
            <h1 className="text-lg font-bold text-slate-900">Novo Cliente</h1>
            <p className="text-xs text-slate-500">Criar uma nova instância isolada do sistema</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit}>
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-indigo-600" />
                Dados do cliente
              </CardTitle>
              <CardDescription>
                Um cliente é uma instalação isolada com seus próprios usuários, lideranças, links e dados.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome do cliente *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="Ex: Rede Flávia"
                    required
                    data-testid="tenant-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="slug">Slug (URL pública)</Label>
                  <Input
                    id="slug"
                    value={formData.slug}
                    onChange={(e) => handleChange('slug', makeSlugPreview(e.target.value))}
                    placeholder="auto-gerado a partir do nome"
                    data-testid="tenant-slug-input"
                  />
                  <p className="text-xs text-slate-500">
                    Links públicos: <code className="bg-slate-100 px-1.5 py-0.5 rounded">/{slugPreview || 'slug'}/r/&lt;token&gt;</code>
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="primary_color">Cor principal</Label>
                  <div className="flex gap-2 items-center">
                    <Input
                      id="primary_color"
                      type="color"
                      value={formData.primary_color}
                      onChange={(e) => handleChange('primary_color', e.target.value)}
                      className="w-16 h-10 p-1 cursor-pointer"
                      data-testid="tenant-color-input"
                    />
                    <Input
                      value={formData.primary_color}
                      onChange={(e) => handleChange('primary_color', e.target.value)}
                      placeholder="#1e3a8a"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_email">E-mail de contato</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => handleChange('contact_email', e.target.value)}
                    placeholder="contato@cliente.com.br"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="observacoes">Observações internas</Label>
                <Textarea
                  id="observacoes"
                  value={formData.observacoes}
                  onChange={(e) => handleChange('observacoes', e.target.value)}
                  placeholder="Notas internas sobre o cliente..."
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200 mt-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <KeyRound className="h-5 w-5 text-indigo-600" />
                Administrador do cliente
              </CardTitle>
              <CardDescription>
                Defina o login e senha do admin do cliente. A conta é criada já ativa — ele pode fazer login imediatamente.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="admin_name">Nome do administrador *</Label>
                <Input
                  id="admin_name"
                  value={formData.admin_name}
                  onChange={(e) => handleChange('admin_name', e.target.value)}
                  placeholder="Nome completo"
                  required
                  data-testid="tenant-admin-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin_email">E-mail do administrador *</Label>
                <Input
                  id="admin_email"
                  type="email"
                  value={formData.admin_email}
                  onChange={(e) => handleChange('admin_email', e.target.value)}
                  placeholder="admin@cliente.com.br"
                  required
                  data-testid="tenant-admin-email-input"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="admin_password">Senha do administrador *</Label>
                  <button
                    type="button"
                    onClick={handleGeneratePassword}
                    className="text-xs text-indigo-600 hover:text-indigo-700 inline-flex items-center gap-1"
                    data-testid="generate-password-btn"
                  >
                    <Sparkles className="h-3 w-3" />
                    Gerar senha forte
                  </button>
                </div>
                <div className="relative">
                  <Input
                    id="admin_password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.admin_password}
                    onChange={(e) => handleChange('admin_password', e.target.value)}
                    placeholder="Mínimo 8 caracteres"
                    minLength={8}
                    required
                    autoComplete="new-password"
                    className="pr-10 font-mono"
                    data-testid="tenant-admin-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500">
                  ⚠️ Anote esta senha antes de criar o cliente. Você precisará compartilhá-la com o admin via WhatsApp/canal seguro.
                </p>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-3 mt-6">
            <Button type="button" variant="outline" onClick={() => navigate('/platform')}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading} data-testid="submit-tenant-btn">
              {loading ? 'Criando...' : 'Criar cliente'}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
};
